import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getRegions, getRegionStatistics, upsertRegionStatistic, deleteAllRegionStatistics } from '../lib/api';
import { Button } from '../components/ui/button';
import { Save, Check, MapPin, Banknote, Users, PiggyBank, Trash2 } from 'lucide-react';

const EMPTY_LOANS = {
  numberOfSocialLoans: 0, totalLoansForSocial: 0,
  numberOfFreeLoans: 0, totalLoansForFree: 0,
  numberOfProjectsLoans: 0, totalLoansForProjects: 0,
  numberOfFamilyLoans: 0, totalLoansForFamilies: 0,
  totalNumberOfLoans: 0, totalLoans: 0,
};
const EMPTY_EMP = {
  totalnumberofbeneficiaries: 0, Qualificationandcapacitybuilding: 0,
  Productionincubatorsandcommunitykitchens: 0, Temporaryandpermanentsalesoutlets: 0,
  totalSecondStatics: 0,
};
const EMPTY_SAV = {
  valueofsavings: 0, numberofbeneficiaries: 0,
  numberofaccounts: 0, savingsdistributionratio: 0,
};

const LOAN_FIELDS = [
  { key: 'numberOfSocialLoans', ar: 'عدد القروض الاجتماعية', type: 'int' },
  { key: 'totalLoansForSocial', ar: 'إجمالي التمويل الاجتماعي', type: 'float' },
  { key: 'numberOfFreeLoans', ar: 'عدد قروض العمل الحر', type: 'int' },
  { key: 'totalLoansForFree', ar: 'إجمالي تمويل العمل الحر', type: 'float' },
  { key: 'numberOfProjectsLoans', ar: 'عدد قروض المشاريع', type: 'int' },
  { key: 'totalLoansForProjects', ar: 'إجمالي تمويل المشاريع', type: 'float' },
  { key: 'numberOfFamilyLoans', ar: 'عدد قروض الأسر', type: 'int' },
  { key: 'totalLoansForFamilies', ar: 'إجمالي تمويل الأسر', type: 'float' },
  { key: 'totalNumberOfLoans', ar: 'إجمالي عدد القروض', type: 'int' },
  { key: 'totalLoans', ar: 'إجمالي التمويل', type: 'float' },
];
const EMP_FIELDS = [
  { key: 'totalnumberofbeneficiaries', ar: 'إجمالي عدد المستفيدين' },
  { key: 'Qualificationandcapacitybuilding', ar: 'التأهيل وبناء القدرات' },
  { key: 'Productionincubatorsandcommunitykitchens', ar: 'الحاضنات الإنتاجية والمطابخ المجتمعية' },
  { key: 'Temporaryandpermanentsalesoutlets', ar: 'منافذ البيع المؤقتة والدائمة' },
  { key: 'totalSecondStatics', ar: 'إجمالي التمكين والتنمية' },
];
const SAV_FIELDS = [
  { key: 'valueofsavings', ar: 'قيمة الادخار', type: 'float' },
  { key: 'numberofbeneficiaries', ar: 'عدد المستفيدين' },
  { key: 'numberofaccounts', ar: 'عدد الحسابات' },
  { key: 'savingsdistributionratio', ar: 'نسبة توزيع الادخار', type: 'float' },
];

function FieldInput({ label, value, onChange, type }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input
        type="number"
        step={type === 'float' ? '0.01' : '1'}
        value={value || 0}
        onChange={e => onChange(type === 'float' ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0A4C3A]/20 focus:border-[#0A4C3A] outline-none transition"
        data-testid={`field-${label}`}
      />
    </div>
  );
}

export default function RegionStatisticsPage() {
  const { t } = useLanguage();
  const [regions, setRegions] = useState([]);
  const [allStats, setAllStats] = useState({});
  const [selectedRegion, setSelectedRegion] = useState('');
  const [loans, setLoans] = useState({ ...EMPTY_LOANS });
  const [emp, setEmp] = useState({ ...EMPTY_EMP });
  const [sav, setSav] = useState({ ...EMPTY_SAV });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load regions and existing stats
  useEffect(() => {
    (async () => {
      try {
        const [regRes, statsRes] = await Promise.all([getRegions(), getRegionStatistics()]);
        setRegions(regRes.data || []);
        const statsMap = {};
        (statsRes.data || []).forEach(s => { statsMap[s.region_id] = s; });
        setAllStats(statsMap);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // When region changes, load its data
  useEffect(() => {
    if (!selectedRegion) {
      setLoans({ ...EMPTY_LOANS });
      setEmp({ ...EMPTY_EMP });
      setSav({ ...EMPTY_SAV });
      return;
    }
    const existing = allStats[selectedRegion];
    if (existing) {
      setLoans({ ...EMPTY_LOANS, ...existing.loans });
      setEmp({ ...EMPTY_EMP, ...existing.empowerment_development });
      setSav({ ...EMPTY_SAV, ...existing.savings });
    } else {
      setLoans({ ...EMPTY_LOANS });
      setEmp({ ...EMPTY_EMP });
      setSav({ ...EMPTY_SAV });
    }
    setSaved(false);
  }, [selectedRegion, allStats]);

  const handleSave = useCallback(async () => {
    if (!selectedRegion) return;
    setSaving(true);
    try {
      await upsertRegionStatistic({
        region_id: selectedRegion,
        loans,
        empowerment_development: emp,
        savings: sav,
      });
      setAllStats(prev => ({
        ...prev,
        [selectedRegion]: { region_id: selectedRegion, loans, empowerment_development: emp, savings: sav }
      }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [selectedRegion, loans, emp, sav]);

  const updateLoan = (key, val) => setLoans(prev => ({ ...prev, [key]: val }));
  const updateEmp = (key, val) => setEmp(prev => ({ ...prev, [key]: val }));
  const updateSav = (key, val) => setSav(prev => ({ ...prev, [key]: val }));

  const hasData = (rid) => !!allStats[rid];

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="region-statistics-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold text-gray-900">إحصائيات بنك التنمية الاجتماعية</h1>
        <Button
          variant="destructive"
          data-testid="delete-all-stats-btn"
          onClick={async () => {
            if (!window.confirm('هل تريد مسح جميع الإحصائيات؟ لا يمكن التراجع!')) return;
            try {
              const { data } = await deleteAllRegionStatistics();
              alert(`تم مسح ${data.deleted} إحصائية`);
              window.location.reload();
            } catch { alert('فشل المسح'); }
          }}
        >
          <Trash2 className="w-4 h-4 me-2" />مسح الإحصائيات
        </Button>
      </div>
      <p className="text-sm text-gray-500 mb-6">إدارة الإحصائيات الرسمية حسب المنطقة</p>

      {/* Region selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="w-4 h-4 inline-block me-1" />
          اختر المنطقة
        </label>
        <select
          value={selectedRegion}
          onChange={e => setSelectedRegion(e.target.value)}
          className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0A4C3A]/20 focus:border-[#0A4C3A] outline-none"
          data-testid="region-selector"
        >
          <option value="">— اختر منطقة —</option>
          {regions.map(r => (
            <option key={r.id} value={r.id}>
              {r.name_ar} {hasData(r.id) ? '✓' : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedRegion && (
        <div className="space-y-6">
          {/* التمويل */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm" data-testid="loans-section">
            <h2 className="flex items-center gap-2 text-base font-bold text-amber-700 mb-4">
              <Banknote className="w-5 h-5" />
              التمويل
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {LOAN_FIELDS.map(f => (
                <FieldInput key={f.key} label={f.ar} value={loans[f.key]} type={f.type}
                  onChange={v => updateLoan(f.key, v)} />
              ))}
            </div>
          </div>

          {/* التمكين والتنمية */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm" data-testid="empowerment-section">
            <h2 className="flex items-center gap-2 text-base font-bold text-blue-700 mb-4">
              <Users className="w-5 h-5" />
              التمكين والتنمية
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {EMP_FIELDS.map(f => (
                <FieldInput key={f.key} label={f.ar} value={emp[f.key]} type={f.type || 'int'}
                  onChange={v => updateEmp(f.key, v)} />
              ))}
            </div>
          </div>

          {/* الادخار */}
          <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm" data-testid="savings-section">
            <h2 className="flex items-center gap-2 text-base font-bold text-emerald-700 mb-4">
              <PiggyBank className="w-5 h-5" />
              الادخار
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {SAV_FIELDS.map(f => (
                <FieldInput key={f.key} label={f.ar} value={sav[f.key]} type={f.type || 'int'}
                  onChange={v => updateSav(f.key, v)} />
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D] text-white px-6"
              data-testid="save-stats-btn"
            >
              {saved ? <><Check className="w-4 h-4 me-2" /> تم الحفظ</> : 
               saving ? 'جاري الحفظ...' : <><Save className="w-4 h-4 me-2" /> حفظ</>}
            </Button>
          </div>
        </div>
      )}

      {!selectedRegion && (
        <div className="text-center py-16 text-gray-400">
          <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">اختر منطقة لعرض وتعديل الإحصائيات</p>
        </div>
      )}
    </div>
  );
}
